import React, { useState, createElement } from 'react';
import { SearchIcon, DownloadIcon, FileIcon, FolderIcon, ChevronDownIcon, ChevronRightIcon, CopyIcon, CheckIcon, Loader2Icon } from 'lucide-react';
import { ASSETS, EXTERNAL_IMAGES, cleanAssetPath } from '../../config/assets';
export function AssetsTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({
    'Branding & Logos': true
  });
  const toggleFolder = (folder: string) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folder]: !prev[folder]
    }));
  };
  const copyUrl = (name: string) => {
    navigator.clipboard.writeText(cleanAssetPath(name));
    setCopiedUrl(name);
    setTimeout(() => setCopiedUrl(null), 1500);
  };
  const downloadAsset = async (url: string, name: string) => {
    try {
      setDownloading(name);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setDownloading(null);
    }
  };
  // Helper to determine file type from extension
  const getFileType = (name: string): string => {
    if (name.endsWith('.svg')) return 'image/svg+xml';
    if (name.endsWith('.png')) return 'image/png';
    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
    return 'application/octet-stream';
  };
  const getFileSize = (name: string): string => {
    if (name.endsWith('.svg')) return 'SVG';
    if (name.endsWith('.png')) return 'PNG';
    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'JPG';
    return 'FILE';
  };
  // Build asset folders from centralized config
  const assetsByFolder: Record<string, string[]> = {
    'Branding & Logos': ['sj-website-logo-colour.svg', 'sj-website-logo-white.svg', 'sj-website-sj-text-header.svg', 'sj-website-sj-text-stacked-white.svg', 'sj-website-sj-text-stacked-colour.svg'],
    'Homepage - Hero Banner': ['sj-website-home-banner-1.png', 'sj-website-home-banner-2.png', 'sj-website-home-banner-3.png', 'sj-website-home-banner-4.png', 'sj-website-home-banner-5.png'],
    'Homepage - BookOnline Slider': ['smith-jengs-bathroom.jpg', 'smith-jengs-raomubashir14.jpg', 'smith-jengs-peter-vang.jpg', 'smith-jengs-curtis-adams.jpg', 'smith-jengs-2-mirrors.jpg'],
    'Homepage - OurServices': ['sj-website-home-img1.png'],
    'Homepage - HousekeepingRevolution': ['yellow-armchair-living-room-with-copy-space.jpg', 'smith-jengs-staircase.jpg'],
    'Homepage - FAQ & WhyChooseUs': ['smith-jengs-alexander.jpg', 'smith-jengs-2-mirrors.jpg'],
    'Commercial Cleaning': ['office-photo.jpg', 'sj-website-commercial-img1.png', 'sj-website-commercial-img2.png'],
    'Basic Home Cleaning': ['sj-website-basic-home-banner.png', 'sj-website-basic-home-img1.png', 'sj-website-basic-home-img2.png'],
    'Spring / Deep Cleaning': ['sj-website-deep-clean-banner.png', 'kitchen-clean-dog.png', 'lady-cleaning-window.png'],
    'About Us': ['banner2.jpg', 'sj-website-about-us-img1.png', 'sj-website-icon-vision.svg', 'sj-website-icon-mission.svg', 'sj-website-icon-values.svg', 'sj-website-about-us-img2.png'],
    'Contact Us & FAQ': ['sj-website-contact-us-banner.png', 'sj-website-contact-us-img1.png', 'sj-website-contact-us-map.png', 'sj-website-faq-banner.png']
  };
  // Resolve each folder into full asset objects
  const assets: Record<string, Array<{
    name: string;
    type: string;
    size: string;
    url: string;
  }>> = {};
  for (const [folder, names] of Object.entries(assetsByFolder)) {
    assets[folder] = names.map(name => ({
      name,
      type: getFileType(name),
      size: getFileSize(name),
      url: ASSETS[name] || EXTERNAL_IMAGES[name] || `/assets/${name}`
    }));
  }
  const allItems = Object.values(assets).flat();
  const totalCount = allItems.length;
  return <div className="flex flex-col h-full" data-id="element-89">
      <div className="p-4 border-b border-slate-800" data-id="element-90">
        <div className="relative" data-id="element-91">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-92" />
          <input type="text" placeholder="Search assets..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-md py-2 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" data-id="element-93" />
        </div>
        <div className="text-[10px] text-slate-500 mt-2" data-id="element-94">
          {totalCount} assets across {Object.keys(assets).length} folders
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-95">
        {Object.entries(assets).map(([folder, items]) => <div key={folder} className="space-y-1" data-id="element-96">
            <button onClick={() => toggleFolder(folder)} className="flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white w-full text-left py-1" data-id="element-97">
              {expandedFolders[folder] ? <ChevronDownIcon className="w-4 h-4 text-slate-500" data-id="element-98" /> : <ChevronRightIcon className="w-4 h-4 text-slate-500" data-id="element-99" />}
              <FolderIcon className="w-4 h-4 text-blue-400" data-id="element-100" />
              <span data-id="element-101">{folder}</span>
              <span className="text-xs text-slate-500 ml-auto" data-id="element-102">
                {items.length} items
              </span>
            </button>

            {expandedFolders[folder] && <div className="pl-6 space-y-2 mt-2" data-id="element-103">
                {items.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase())).map(item => <div key={`${folder}-${item.name}`} className="group flex items-center justify-between p-2 rounded-md hover:bg-slate-800 border border-transparent hover:border-slate-700 transition-colors" data-id="element-104">
                      <div className="flex items-center gap-3 overflow-hidden" data-id="element-105">
                        <div className="w-8 h-8 rounded bg-slate-900 flex-shrink-0 overflow-hidden border border-slate-700 flex items-center justify-center" data-id="element-106">
                          {item.type.includes('image') ? <img src={item.url} alt={item.name} className="w-full h-full object-cover" data-id="element-107" /> : <FileIcon className="w-4 h-4 text-slate-500" data-id="element-108" />}
                        </div>
                        <div className="min-w-0" data-id="element-109">
                          <div className="text-xs text-slate-200 truncate" data-id="element-110">
                            {item.name}
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono truncate" data-id="element-111">
                            /assets/{item.name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all" data-id="element-112">
                        <button onClick={() => copyUrl(item.name)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-all" title="Copy path" data-id="element-113">
                          {copiedUrl === item.name ? <CheckIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-114" /> : <CopyIcon className="w-3.5 h-3.5" data-id="element-115" />}
                        </button>
                        <button onClick={() => downloadAsset(item.url, item.name)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-all" title="Download" disabled={downloading === item.name} data-id="element-116">
                          {downloading === item.name ? <Loader2Icon className="w-3.5 h-3.5 animate-spin text-blue-400" data-id="element-117" /> : <DownloadIcon className="w-3.5 h-3.5" data-id="element-118" />}
                        </button>
                      </div>
                    </div>)}
              </div>}
          </div>)}
      </div>

      <div className="p-4 border-t border-slate-800 flex-shrink-0" data-id="element-119">
        <button onClick={() => alert('Batch ZIP download requires a server-side endpoint. Individual assets can be downloaded using the download button on each item.')} className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-md flex items-center justify-center gap-2 transition-colors font-medium text-sm border border-slate-700" data-id="element-120">
          <DownloadIcon className="w-4 h-4" data-id="element-121" />
          Download All Assets (ZIP)
        </button>
      </div>
    </div>;
}